import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../store/hooks';
import type { ReactNode } from 'react';

/**
 * Pagina di cambio password obbligatorio. Esportata da qui perché la consumano
 * sia questo guard (per non rimbalzare all'infinito su sé stessa) sia
 * AppRouter, che già importa l'AuthGuard: una sola definizione, nessun modulo
 * in più e nessun import circolare.
 */
export const CHANGE_PASSWORD_PATH = '/change-password';

export function AuthGuard({ children }: { children: ReactNode }) {
  const { status, user } = useAppSelector((state) => state.auth);
  const { t } = useTranslation();
  const location = useLocation();

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="flex justify-center items-center h-screen">
        <p>{t('auth.checkingAuth')}</p>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Liferay marca con `passwordReset` gli utenti che devono cambiare la
  // password al primo accesso. Il flag NON blocca l'autenticazione: la catena
  // OAuth2 ROPC (LiferayResourceOwnerLoginHandler -> authenticate ->
  // _checkPasswordPolicy) controlla lockout e scadenza, mai questo flag, e
  // infatti il token viene emesso regolarmente. Nel portale web sarebbe il
  // livello servlet a dirottare su /update_password, ma una SPA quel livello
  // non lo attraversa mai: il blocco può metterlo solo il client, qui.
  //
  // Il controllo sta nel guard e non nella LoginPage perché deve valere per ogni
  // rotta protetta, non solo per l'istante del login: un URL incollato o un link
  // profondo passano di qui e non da lì.
  //
  // Nota su cosa NON copre: al reload `checkAuth` chiama /user/init, che con il
  // flag alzato risponde 403 come tutte le chiamate autenticate; la sessione
  // viene scartata e si finisce al login. È accettabile — l'utente rifà login e
  // torna qui — ma spiega perché in pratica a questa pagina ci si arriva sempre
  // subito dopo il form di accesso, con user e webId ancora in store (e infatti
  // il cambio imposto ha bisogno di entrambi).
  if (user?.passwordReset && location.pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
